import React, { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import base64 from 'react-native-base64';
import { showMessage } from 'react-native-flash-message';
import { Platform } from 'react-native';

import { I18nContext } from 'i18n/context/I18nContext';
import World from 'components/World';
import Appearances from 'utils/Appearances';
import { logEvent } from 'utils/firebase/analytics';
import { useTutorialVideo } from 'hooks/useTutorialVideo';
import WelcomeVideoModal from 'components/layout/WelcomeVideoModal';
import {
  getResourceIDFromLocation,
  encodeForHTML,
  deepEqual,
} from 'utils/helpers';
import {
  updateTextOverlay,
  removeTextOverlay,
  clearTextOverlays,
  setLabelDisplay,
  clearLevelLoading,
} from 'store/world/actions';
import {
  syncWorldPeerState,
  sendWorldMessage,
  syncPeerSate,
  sendAppearance,
  sendLocation,
} from 'store/communication/actions/controlActions';
import {
  setAvatarLoading,
  setAppearance,
  setAppearanceInStorage,
  setAsdkId,
  forceBroadcast,
} from 'store/appearance';
import { setMeTransform } from 'store/communication/actions/stateActions';
import { clearViewboards, registerViewboards } from 'store/viewboard';
import { setReadyToMove } from 'store/meeting/actions/meeting';
import {
  shouldUpdateTransformReduxState,
  shouldUpdateTransformUnity,
  convertMessageToTransform,
} from 'utils/World/Transform';
import worldStyle from 'style/3d';
import { LEVEL_LOAD } from 'utils/firebase/analytics.config';
import getRemoteConfigValue from 'utils/firebase/remoteConfig';
import {
  QUIKLEARN_BACKEND_URL,
  ALL_CURRICULAM_DETAILS_API,
  API_KEY,
} from 'config/app.config';
import {
  resolveCourseEntryVideoFromRemoteConfig,
  hasSeenCourseEntryVideo,
  markCourseEntryVideoSeen,
} from 'utils/firebase/courseEntryVideos';
import {
  startMetaverseSession,
  clearMetaverseSession,
} from 'utils/tracking/metaverseSession';

const QUIKBASE_SPAWN_SHIFT_VISITED_KEY_PREFIX =
  'quiklearn_quikbase_spawn_shift_visited_v1_';
const QUIKBASE_SPAWN_SHIFT_CONTEXT_KEY_PREFIX =
  'quiklearn_quikbase_spawn_shift_context_v1_';

function getQuikbaseSpawnShiftKey(prefix, userId) {
  const suffix = userId ? String(userId) : 'anon';
  return `${prefix}${suffix}`;
}

function readLocalStorageJson(key) {
  try {
    const raw = window?.localStorage?.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

function writeLocalStorageJson(key, value) {
  try {
    window?.localStorage?.setItem(key, JSON.stringify(value));
  } catch (_) {
    // ignore storage failures (private mode / quota / disabled)
  }
}

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('timeout')), ms),
    ),
  ]);
}

async function fetchCurriculumGradeAndSubject({
  curriculumId,
  accessToken,
}) {
  if (!curriculumId || !accessToken) return null;
  const base = String(QUIKLEARN_BACKEND_URL || '').replace(/\/$/, '');
  const endpoint = String(ALL_CURRICULAM_DETAILS_API || '').replace(
    ':id',
    encodeURIComponent(String(curriculumId)),
  );
  const response = await fetch(`${base}${endpoint}`, {
    headers: {
      'x-api-key': API_KEY,
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!response.ok) {
    throw new Error(`curriculum details failed (${response.status})`);
  }
  const json = await response.json();
  const curriculum = json?.curriculum;
  const grade = curriculum?.grade;
  const subject = curriculum?.subject;
  if (!grade || !subject) return null;
  return { grade, subject };
}
function getAddressablesPlatformName() {
  switch (Platform.OS) {
    case 'android':
      return 'Android';
    case 'ios':
      return 'iOS';
    case 'web':
      return 'WebGL';
    default:
      console.warn('Unexpected OS found: ', Platform.OS);
      return '';
  }
}
const MeetingWorld = () => {
  const [firstLoad, setFirstLoad] = useState(true);
  const dispatch = useDispatch();
  const location = useSelector(state => state.world.location);
  const teleportToPeer = useSelector(state => state.world.teleportToPeer);
  const showFloatingNames = useSelector(state => state.world.showFloatingNames);
  const loadStarted = useSelector(state => state.world.loadStarted);
  const curriculumGradeAndSubject = useSelector(
    state => state.course.curriculumGradeAndSubject,
  );

  // Tutorial video modal for first-time Quikbase users (web only)
  const userIdForTutorial = useSelector(state => state.app?.courseTokenValues?.userId);
  const {
    isTutorialVideoOpen,
    showTutorialVideo,
    showTutorialVideoIfNotSeen,
    hideTutorialVideo,
  } = useTutorialVideo(userIdForTutorial);
  const [isCourseEntryVideoOpen, setIsCourseEntryVideoOpen] = useState(false);
  const [courseEntryVideoConfig, setCourseEntryVideoConfig] = useState(null);
  const courseEntryVideoTimeoutRef = React.useRef(null);

  const hideCourseEntryVideo = () => {
    const scopeKey = courseEntryVideoConfig?.scopeKey;
    if (scopeKey) {
      markCourseEntryVideoSeen({
        userId: userIdForTutorial,
        scopeKey,
      });
    }
    setIsCourseEntryVideoOpen(false);
    setCourseEntryVideoConfig(null);
  };

  const maybeShowCourseEntryVideo = async () => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    if (isQuikbaseWorld) return;
    if (isCourseEntryVideoOpen) return;

    const curriculumId = curriculumIdFromToken || courseData?.curriculumId;
    let grade = curriculumGradeAndSubject?.grade;
    let subject = curriculumGradeAndSubject?.subject;

    let resolvedVideoConfig = resolveCourseEntryVideoFromRemoteConfig({
      curriculumId,
      grade,
      subject,
    });

    // If match-by-grade/subject failed because context is still loading,
    // fetch curriculum metadata directly as a fallback.
    if (!resolvedVideoConfig && curriculumId && accessToken) {
      try {
        const fetched = await withTimeout(
          fetchCurriculumGradeAndSubject({
            curriculumId,
            accessToken,
          }),
          2500,
        );
        if (fetched?.grade && fetched?.subject) {
          grade = fetched.grade;
          subject = fetched.subject;
          resolvedVideoConfig = resolveCourseEntryVideoFromRemoteConfig({
            curriculumId,
            grade,
            subject,
          });
        }
      } catch (_) {
        // Ignore fallback failures; no-op if we can't resolve a configured video.
      }
    }

    if (!resolvedVideoConfig?.scopeKey) return;

    const alreadySeen = hasSeenCourseEntryVideo({
      userId: userIdForTutorial,
      scopeKey: resolvedVideoConfig.scopeKey,
    });

    if (alreadySeen) return;

    setCourseEntryVideoConfig(resolvedVideoConfig);
    setIsCourseEntryVideoOpen(true);
  };

  useEffect(() => {
    return () => {
      if (courseEntryVideoTimeoutRef.current) {
        clearTimeout(courseEntryVideoTimeoutRef.current);
        courseEntryVideoTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const handleOpenTutorial = () => showTutorialVideo();
    window.addEventListener('quiklearn:show-tutorial-video', handleOpenTutorial);
    return () => {
      window.removeEventListener('quiklearn:show-tutorial-video', handleOpenTutorial);
    };
  }, [showTutorialVideo]);

  const lowPowerMode = useSelector(
    state => state.meeting.settings.lowPowerMode,
  );
  const meTransformData = useSelector(
    state => state.communication.me.transform,
  );
  const peers = useSelector(state => state.communication.peers);
  const { translate } = useContext(I18nContext);

  const [floatingNames, setFloatingNames] = useState(showFloatingNames);
  useEffect(() => {
    setFloatingNames(showFloatingNames);
  }, [showFloatingNames]);

  const forceAppearanceBroadcast = useSelector(
    state => state.appearance.forceBroadcast,
  );
  useEffect(() => {
    if (forceAppearanceBroadcast) {
      World.runCommand('save_appearance');
      dispatch(forceBroadcast(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceAppearanceBroadcast]);

  const locations = useSelector(state => state.meeting.properties.locations);
  const [locationResourceID, setLocationResourceID] = useState(
    getResourceIDFromLocation(location, locations),
  );
  useEffect(() => {
    if (location) {
      setLocationResourceID(getResourceIDFromLocation(location, locations));
    }
  }, [location, locations]);

  const looks = useSelector(state => state.appearance.looks);
  const body = useSelector(state => state.appearance.body);
  const look = looks[body];
  const asdkId = useSelector(state => state.appearance.asdkId);
  const isFullBody = useSelector(state => state.appearance.isFullBody);

  const AITeacherToLoad = useSelector(
    state => state.app.courseTokenValues.AITeacherToLoad,
  );
  const courseContentUrl = useSelector(
    state => state.app.courseTokenValues.courseContentUrl,
  );
  const courseProgress = useSelector(
    state => state.app.courseTokenValues.courseProgress,
  );
  const userId = useSelector(state => state.app.courseTokenValues.userId);
  const accessToken = useSelector(state => state.meeting?.properties?.token);
  const curriculumIdFromToken = useSelector(
    state => state.app?.courseTokenValues?.courseId,
  );
  const worldToLoad = useSelector(state => state.app.courseTokenValues.worldToLoad);
  const collectEmails = useSelector(
    state => state.app.courseTokenValues.collectEmails,
  );
  const { courseData } = useSelector(state => state.course);

  const legacy = useSelector(state => state.meeting.meeting.legacy);
  const customCourse = useSelector(state => state.meeting.meeting.customCourse);
  // IMPORTANT: determine Quikbase from the token (available immediately),
  // not from meeting.isQuikbase (which is set later after course fetch).
  const isQuikbaseWorld = worldToLoad === 'quikbase' && !customCourse;

  const onLoadComplete = async () => {
    // Metaverse session tracking:
    // - Start a new session when a metaverse finishes loading
    // - Keep same session while user stays in that metaverse
    // - Do not track sessions in Quikbase (or avatar customization screen, which is separate)
    const isQuikbaseEffective = location === 'quikbase' || isQuikbaseWorld;
    if (isQuikbaseEffective) {
      clearMetaverseSession();
    } else {
      startMetaverseSession({ location, excluded: false });
    }

    if (courseEntryVideoTimeoutRef.current) {
      clearTimeout(courseEntryVideoTimeoutRef.current);
      courseEntryVideoTimeoutRef.current = null;
    }

    setFirstLoad(true);
    dispatch(clearTextOverlays());
    const addressablesBaseUrl = getRemoteConfigValue(
      'tutors_addressables_catalog_base_url',
    ).asString();
    const addressablesCatalog = `${addressablesBaseUrl}${getAddressablesPlatformName()}/catalog_view.json`;

    if (!isQuikbaseWorld) {
      World.runCommand(`initialize_avatar_system ${addressablesCatalog}`); // It actually starts Addressable system which is not needed in quikbase.
    }
    console.log(
      'initialize_avatar_system',
      `initialize_avatar_system ${addressablesCatalog}`,
    );

    World.runCommand('set_logger true 2');
    World.runCommand('set_overlay_z_index_delta 10000000');
    World.runCommand('set_overlay_distance_delta 0.0005');
    dispatch(sendLocation(location));
    // set the loading views
    dispatch(setAvatarLoading(true));
    if (teleportToPeer) {
      World.runCommand(`teleport_user ${teleportToPeer}`);
    }

    console.log('AITeacherToLoad from MeetingWorld:', AITeacherToLoad);
    console.log('courseContentUrl from MeetingWorld:', courseContentUrl);
    console.log('courseProgress from MeetingWorld:', courseProgress);

    const AITeacherToLoadWithUnderscores = AITeacherToLoad.replace(/\s+/g, '_');

    console.log(
      'AITeacherToLoadNoSpaces in onUnityReadyToLoadLevel:',
      AITeacherToLoadWithUnderscores,
    );

    if (isQuikbaseWorld) {
      setIsCourseEntryVideoOpen(false);
      setCourseEntryVideoConfig(null);

      // Quikbase spawn shifting rule:
      // - First-ever Quikbase entry for a user: DO NOT shift (keep centered).
      // - Every later entry: shift, regardless of navigation source (dashboard vs other worlds).
      //
      // We also persist the most-recent grade/subject context so we can shift even when
      // the user re-enters Quikbase without shift params in the URL.
      const isWeb = Platform.OS === 'web' && typeof window !== 'undefined';
      const visitedKey = isWeb
        ? getQuikbaseSpawnShiftKey(
            QUIKBASE_SPAWN_SHIFT_VISITED_KEY_PREFIX,
            userId,
          )
        : null;
      const contextKey = isWeb
        ? getQuikbaseSpawnShiftKey(
            QUIKBASE_SPAWN_SHIFT_CONTEXT_KEY_PREFIX,
            userId,
          )
        : null;

      let hasVisitedQuikbase = false;
      let gradeFromUrl;
      let subjectFromUrl;
      if (isWeb) {
        try {
          const params = new URLSearchParams(window.location.search);
          gradeFromUrl = params.get('shiftGrade') || undefined;
          subjectFromUrl = params.get('shiftSubject') || undefined;
        } catch (_) {
          // ignore URL parsing failures
        }

        try {
          hasVisitedQuikbase = window.localStorage?.getItem(visitedKey) === 'true';
        } catch (_) {
          hasVisitedQuikbase = false;
        }
      }

      const storedContext =
        isWeb && contextKey ? readLocalStorageJson(contextKey) : null;

      // Prefer grade/subject derived from backend curriculum details API.
      // This is normally populated by Meeting->fetchCourseData(), but may not
      // be ready by the time Unity finishes loading Quikbase.
      let grade =
        curriculumGradeAndSubject?.grade || gradeFromUrl || storedContext?.grade;
      let subject =
        curriculumGradeAndSubject?.subject ||
        subjectFromUrl ||
        storedContext?.subject;

      // Persist last-known context when we have it (helps dashboard entries shift correctly)
      if (isWeb && contextKey && grade && subject) {
        writeLocalStorageJson(contextKey, {
          grade: String(grade),
          subject: String(subject),
          ts: Date.now(),
        });
      }

      const shouldShiftSpawnPoint = Boolean(hasVisitedQuikbase);
      if (shouldShiftSpawnPoint) {
        // If Redux hasn't populated grade/subject yet, fetch curriculum details directly.
        if ((!grade || !subject) && isWeb) {
          try {
            const curriculumId = curriculumIdFromToken || courseData?.curriculumId;
            const fetched = await withTimeout(
              fetchCurriculumGradeAndSubject({
                curriculumId,
                accessToken,
              }),
              2500,
            );
            if (fetched?.grade && fetched?.subject) {
              grade = fetched.grade;
              subject = fetched.subject;
              if (contextKey) {
                writeLocalStorageJson(contextKey, {
                  grade: String(grade),
                  subject: String(subject),
                  ts: Date.now(),
                  source: 'api',
                });
              }
            }
          } catch (e) {
            console.warn('[MeetingWorld] Quikbase: curriculum details fetch failed', {
              message: e?.message || String(e),
            });
          }
        }

        if (grade && subject) {
          World.runCommand(
            `shift_spawn_point_to_curriculum_portal ${grade} ${subject}`,
          );
        } else {
          console.warn(
            '[MeetingWorld] Skipping shift_spawn_point_to_curriculum_portal due to missing grade/subject',
            {
              grade,
              subject,
              gradeFromUrl,
              subjectFromUrl,
              storedContext,
              curriculumGradeAndSubject,
            },
          );
        }
      } else {
        console.log(
          '[MeetingWorld] Quikbase: skipping shift spawn (first entry)',
        );

        // Fire-and-forget: prefetch context so the *next* entry can shift even if
        // Redux hasn't populated grade/subject yet at Unity load time.
        if (isWeb && accessToken) {
          const curriculumId = curriculumIdFromToken || courseData?.curriculumId;
          fetchCurriculumGradeAndSubject({ curriculumId, accessToken })
            .then(fetched => {
              if (!fetched?.grade || !fetched?.subject) return;
              if (!contextKey) return;
              writeLocalStorageJson(contextKey, {
                grade: String(fetched.grade),
                subject: String(fetched.subject),
                ts: Date.now(),
                source: 'api_prefetch',
              });
            })
            .catch(() => {});
        }
      }

      // Mark Quikbase as visited (so next entry shifts). Do this regardless of shift success.
      if (isWeb && visitedKey && !hasVisitedQuikbase) {
        try {
          window.localStorage?.setItem(visitedKey, 'true');
        } catch (_) {
          // ignore
        }
      }
      // Ensure Quikbase reaches the same bridge contract as other worlds:
      // Unity will typically emit ready_to_move, but Quikbase can skip tutor setup
      // so we proactively signal and update Redux for UI overlays.
      World.runCommand('ready_to_move');
      dispatch(setReadyToMove(true));

      // Show tutorial video modal for first-time Quikbase users (web only)
      if (Platform.OS === 'web') {
        // Small delay to ensure world is fully rendered before showing modal
        setTimeout(() => {
          const storageKey = userIdForTutorial
            ? `quiklearn_tutorial_video_seen_${userIdForTutorial}`
            : 'quiklearn_tutorial_video_seen_';
          const hasSeenValue = localStorage.getItem(storageKey);
          console.log('[MeetingWorld] Tutorial video check:', {
            userId: userIdForTutorial,
            storageKey,
            hasSeenValue,
            willShow: hasSeenValue !== 'true',
          });
          showTutorialVideoIfNotSeen();
        }, 5000);
      }
      return;
    }

    World.runCommand(
      `setup_ai_tutor ${courseContentUrl} ${AITeacherToLoadWithUnderscores} ${courseProgress} ${userId} ${collectEmails}`,
    );

    if (Platform.OS === 'web') {
      // Delay so world render and tutor setup are complete before opening the modal.
      courseEntryVideoTimeoutRef.current = setTimeout(() => {
        void maybeShowCourseEntryVideo();
      }, 5000);
    }

    dispatch(clearViewboards());
    dispatch(registerViewboards(locationResourceID));
    if (loadStarted !== -1) {
      logEvent(LEVEL_LOAD, {
        level: location,
        duration: Date.now() - loadStarted,
      });
      dispatch(clearLevelLoading());
    }

    // sync peer state with all other existing peers
    dispatch(syncWorldPeerState());
  };

  const onLegacyFromWorld = message => {
    const { route, payload } = message;

    if (route === 'e') {
      dispatch(sendAppearance(payload));
    } else if (route === 't') {
      // Text overlays are only rendered when floating names are enabled.
      // When disabled, ignore Unity overlay frames (they can be extremely chatty,
      // especially during viewboard fullscreen transitions).
      if (showFloatingNames) {
        handleOverlayMessage(payload);
      }
    } else if (route === 'n') {
      handleLabelMessage(payload);
    } else if (route === 'p') {
      const player_variables = payload.split('|');
      switch (player_variables[0]) {
        case 'rpmReady':
          console.log('Usman RPM ready recieved');
          dispatch(setAvatarLoading(false));
          break;
        case 'updatedAppearance':
          // player var for knowing when unity has finished updating an avatar.
          // process the updated appearance
          const saveToLocalStorage = false;
          const appearance = player_variables[1];
          // save new apperance data to redux store (but not to local storage yet)
          processAppearanceFromUnity(appearance, saveToLocalStorage);
          // broadcast appearance to others in game ONLY if it's their first load into the game
          if (firstLoad) {
            setFirstLoad(false);
            Appearances.broadcastAppearance(body, looks);
          }
          break;
        case 'asdkPlayerId':
          // player var for knowing the users asdkId from unity when they first start customizing.
          const newAsdkId = player_variables[1];
          saveUnityAsdkId(newAsdkId);
          // process the newAsdkId
          break;
        default:
          console.warn(
            'Unrecognized local player variable: ',
            player_variables[0],
          );
      }
    } else {
      dispatch(sendWorldMessage(message));
    }

    if (route === 'd') {
      // REQUEST REMOTE USER APPEARANCE
      dispatch(syncPeerSate(payload));
    }
  };

  const onTransformFromWorld = message => {
    const newTransformData = convertMessageToTransform(message?.payload);
    if (
      shouldUpdateTransformReduxState({
        oldTransformData: meTransformData,
        newTransformData,
      })
    ) {
      dispatch(setMeTransform(message));
    }

    if (
      shouldUpdateTransformUnity({
        oldTransformData: meTransformData,
        newTransformData,
      })
    ) {
      dispatch(sendWorldMessage(message));
    }
  };

  const getLabelInfo = label => {
    if (!label || label === '') {
      return null;
    }

    const sanitizedLabel = label.replace(/_([0-9]+)?/g, '');

    for (const loc of locations) {
      if (loc.id === location) {
        const locLabels = loc.labels;
        let info = null;
        if (typeof locLabels !== 'undefined' && locLabels) {
          info = locLabels[label] || locLabels[sanitizedLabel] || null;
        }
        return info;
      }
    }

    return null;
  };

  const handleLabelMessage = payload => {
    const labelInfo = getLabelInfo(payload);

    dispatch(setLabelDisplay(labelInfo));
  };

  const handleOverlayMessage = payload => {
    const parts = payload.split('|');
    const action = parts[0];
    switch (action) {
      case 'set': {
        if (parts.length < 6) {
          console.warn('TextOverlay.Set was incorrectly formatted: ', payload);
          break;
        }
        const id = parts[1];
        const xPos = parseFloat(parts[2]);
        const yPos = parseFloat(parts[3]);
        let text = encodeForHTML(parts[4]);
        const zIdx = parseInt(parts[5], 10);
        const labelStyle = parts[6] ?? 'name'; // Possible values are 'chat', 'name', and 'viewboard', default to 'name'
        const targetData = parts.length >= 8 ? parts[7] : '';

        let targetArray;
        let target;
        let peer;

        // if the text is "..." set it to the user's name instead.
        if (labelStyle === 'name') {
          // check that the targetData exists
          if (targetData !== '') {
            targetArray = targetData.split('.');
            target = targetArray[3];
            peer = peers[target];
          }
          // if the local user's location is the same as the peers location, process the update
          if (peer?.location !== location) {
            break;
          }
          if (text === '...') {
            // get the users nickname from the store
            const displayName =
              typeof peers[target] === 'undefined'
                ? target
                : peers[target].displayName;
            text = encodeForHTML(displayName);
          }
        }
        dispatch(updateTextOverlay(id, text, labelStyle, xPos, yPos, zIdx));
        break;
      }
      case 'remove': {
        const id = parts[1];
        dispatch(removeTextOverlay(id));
        break;
      }
      case 'clear': {
        // No params, just remove all labels
        dispatch(clearTextOverlays());
        break;
      }
      case 'end': {
        // No params, just end frame
        break;
      }
      default: {
        console.warn('Unexpected overlay action: ', payload);
        break;
      }
    }
  };

  const processAppearanceFromUnity = (encodedAppearance, setLocal = false) => {
    // 1 - decode the json
    const decoded = JSON.parse(base64.decode(encodedAppearance));
    // 2 - take a snapshot of the user's selection to be used for comparison / validation
    const localAppearanceSnapshot = JSON.parse(JSON.stringify(looks[body]));
    // 3 - create a newLooks object to be used in case any changes to redux are necessary. this is a copy of the existing looks object in redux (including all 3 body types).
    const newLooks = { ...looks };

    // 4 -convert decoded appearance from unity to same format as local state appearance
    // create a base object to start from, that mirrors the format we need. Put the head values in right off.
    let convertedUnityAppearance = JSON.parse(
      JSON.stringify(localAppearanceSnapshot),
    );
    convertedUnityAppearance.categories.head = {
      color: '',
      generateHead: false,
      selection: decoded.headAddress,
      texture: '',
    };

    // loop over all the categories in the Unity appearance to save them to the new format
    // This only includes bottom, shoes, top, (no head data)
    const unityCategories = decoded.clothingData;

    for (const category in unityCategories) {
      convertedUnityAppearance.categories[category].selection = unityCategories[
        category
      ].clothingAddress
        ? unityCategories[category].clothingAddress
        : '';
      convertedUnityAppearance.categories[category].texture = unityCategories[
        category
      ].textureAddress
        ? unityCategories[category].textureAddress
        : '';
      convertedUnityAppearance.categories[category].color = unityCategories[
        category
      ].color
        ? unityCategories[category].color
        : '';
    }

    // 5 - Now check the unity received data against the users recent selections in redux
    // the texture/generateHead piece of the head category in the local storage seems to missing often for some reason.  Add it back in to be sure it matches (head texture doesn't actually matter for this validation).
    localAppearanceSnapshot.categories.head.texture = '';
    localAppearanceSnapshot.categories.head.generateHead = false;

    if (!deepEqual(convertedUnityAppearance, localAppearanceSnapshot)) {
      // if the bodies do not match, then there was a problem. Let the user know.
      showMessage({
        message: translate('avatarPreview.avatarUpdateProblem'),
        icon: 'auto',
        autoHide: false,
        type: 'danger',
        floating: true,
        style: { width: 250 },
        titleStyle: { alignSelf: 'center' },
        hideOnPress: true,
        position: 'center',
      });
      // save the new appearance data to redux
      newLooks[body] = convertedUnityAppearance;
      dispatch(setAppearance(newLooks));
    }

    // 6 - save to local storage if indicated
    if (setLocal) {
      dispatch(setAppearanceInStorage(asdkId, body, newLooks));
    }

    // 7 - update the store loading back to false to show the UI again
    dispatch(setAvatarLoading(false));
  };

  const saveUnityAsdkId = encodedAsdkId => {
    // get the user ID from the message
    const userAsdkId = base64.decode(encodedAsdkId);
    // save the user ID to the redux store appearance
    dispatch(setAsdkId(userAsdkId));
  };

  return location && locationResourceID ? (
    <>
      <World
        location={locationResourceID}
        locationFilter={location}
        onLoadComplete={onLoadComplete}
        onTransformFromWorld={onTransformFromWorld}
        onLegacyFromWorld={onLegacyFromWorld}
        lowPower={lowPowerMode}
        showFloatingNames={floatingNames}
        style={worldStyle.world}
        dispatch={dispatch}
      />
      {/* Tutorial video modal for first-time Quikbase users (web only) */}
      {Platform.OS === 'web' && (
        <>
          <WelcomeVideoModal
            isOpen={isTutorialVideoOpen}
            onClose={hideTutorialVideo}
            showCloseButton={true}
            isFromOnboarding={true}
          />
          <WelcomeVideoModal
            isOpen={isCourseEntryVideoOpen}
            onClose={hideCourseEntryVideo}
            showCloseButton={true}
            videoOverride={courseEntryVideoConfig}
          />
        </>
      )}
    </>
  ) : null;
};

export default MeetingWorld;
